import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  DRAG_THRESHOLD_PX,
  EDGE_SNAP_PX,
  MIN_VISIBLE_PX,
  clampToViewport,
  parseStoredPosition,
  snapToEdges,
  toStoredPosition,
  type ViewportSize,
} from './floating';

export interface FloatingControlStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface FloatingControlShellProps {
  /** Unique, non-sensitive key used to persist this control's position. */
  storageKey: string;
  /** Increment to retire saved positions after changing the product default. */
  positionVersion?: number;
  label: string;
  icon: ReactNode;
  children: ReactNode;
  badgeCount?: number;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  side?: 'left' | 'right';
  vertical?: 'top' | 'bottom';
  offsetX?: number;
  offsetY?: number;
  className?: string;
  style?: CSSProperties;
  headerActions?: ReactNode;
  storage?: FloatingControlStorage | null;
  onStorageError?: (error: unknown) => void;
  onPositionChange?: (position: { left: number; top: number }) => void;
}

type Position = { left: number; top: number };
type Size = { width: number; height: number };
type AxisAnchor =
  | { edge: 'start'; gap: number }
  | { edge: 'end'; gap: number }
  | { edge: 'free'; ratio: number };
type PositionAnchor = { horizontal: AxisAnchor; vertical: AxisAnchor };
type DragState = {
  pointerId: number;
  captureTarget: HTMLElement;
  startX: number;
  startY: number;
  originLeft: number;
  originTop: number;
  size: Size;
  startedCollapsed: boolean;
  moved: boolean;
};

const FALLBACK_COLLAPSED_SIZE = { width: 54, height: 54 };
const FALLBACK_EXPANDED_SIZE = { width: 360, height: 300 };
const KEYBOARD_STEP_PX = 16;
const KEYBOARD_FINE_STEP_PX = 4;

function viewportFor(node: HTMLElement): ViewportSize {
  const view = node.ownerDocument.defaultView;
  return {
    width: Math.max(0, view?.innerWidth ?? 0),
    height: Math.max(0, view?.innerHeight ?? 0),
  };
}

function sizeFor(node: HTMLElement, collapsed: boolean): Size {
  const rect = node.getBoundingClientRect();
  const fallback = collapsed ? FALLBACK_COLLAPSED_SIZE : FALLBACK_EXPANDED_SIZE;
  return {
    width: rect.width || fallback.width,
    height: rect.height || fallback.height,
  };
}

function axisAnchor(start: number, size: number, viewport: number): AxisAnchor {
  const endGap = viewport - start - size;
  if (start <= EDGE_SNAP_PX) return { edge: 'start', gap: Math.max(0, start) };
  if (endGap <= EDGE_SNAP_PX) return { edge: 'end', gap: Math.max(0, endGap) };
  return { edge: 'free', ratio: (start + size / 2) / Math.max(1, viewport) };
}

function anchorFromPosition(
  position: Position,
  size: Size,
  viewport: ViewportSize,
): PositionAnchor {
  return {
    horizontal: axisAnchor(position.left, size.width, viewport.width),
    vertical: axisAnchor(position.top, size.height, viewport.height),
  };
}

function positionFromAxisAnchor(anchor: AxisAnchor, size: number, viewport: number): number {
  if (anchor.edge === 'start') return anchor.gap;
  if (anchor.edge === 'end') return viewport - size - anchor.gap;
  return anchor.ratio * viewport - size / 2;
}

function positionFromAnchor(anchor: PositionAnchor, size: Size, viewport: ViewportSize): Position {
  return {
    left: positionFromAxisAnchor(anchor.horizontal, size.width, viewport.width),
    top: positionFromAxisAnchor(anchor.vertical, size.height, viewport.height),
  };
}

function clampShellPosition(
  position: Position,
  size: { width: number; height: number },
  viewport: ViewportSize,
  collapsed: boolean,
  offsetX: number,
  offsetY: number,
): Position {
  if (collapsed) {
    return clampToViewport(position.left, position.top, size, viewport, MIN_VISIBLE_PX);
  }
  // An expanded card must remain usable, not merely leave a 24px sliver on
  // screen. If it is larger than the viewport, pin its leading edge so its
  // header and scrollable content remain reachable.
  const horizontalGutter = Math.max(0, Math.min(offsetX, (viewport.width - size.width) / 2));
  const verticalGutter = Math.max(0, Math.min(offsetY, (viewport.height - size.height) / 2));
  return {
    left: Math.min(
      Math.max(position.left, horizontalGutter),
      Math.max(horizontalGutter, viewport.width - size.width - horizontalGutter),
    ),
    top: Math.min(
      Math.max(position.top, verticalGutter),
      Math.max(verticalGutter, viewport.height - size.height - verticalGutter),
    ),
  };
}

/**
 * Product-neutral floating surface shared by the recorder and extension.
 * Consumers supply all content and import `@vd/annotation-core/floating-shell.css`.
 */
export function FloatingControlShell({
  storageKey,
  positionVersion = 1,
  label,
  icon,
  children,
  badgeCount,
  defaultCollapsed = true,
  collapsed,
  onCollapsedChange,
  side = 'right',
  vertical = 'bottom',
  offsetX = 24,
  offsetY = 24,
  className,
  style,
  headerActions,
  storage,
  onStorageError,
  onPositionChange,
}: FloatingControlShellProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const collapsedButtonRef = useRef<HTMLButtonElement>(null);
  const collapseButtonRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const positionRef = useRef<Position | null>(null);
  const anchorRef = useRef<PositionAnchor | null>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  const onStorageErrorRef = useRef(onStorageError);
  const initializedRef = useRef(false);
  const storageIdentityRef = useRef('');
  const storageSourceRef = useRef<FloatingControlStorage | null | undefined>(storage);
  const focusAfterToggleRef = useRef<'collapsed' | 'expanded' | null>(null);
  const suppressCollapsedClickRef = useRef(false);
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const [position, setPositionState] = useState<Position | null>(null);
  const [dragging, setDragging] = useState(false);
  const isCollapsed = collapsed ?? internalCollapsed;
  onPositionChangeRef.current = onPositionChange;
  onStorageErrorRef.current = onStorageError;

  const reportStorageError = useCallback((error: unknown) => {
    try {
      onStorageErrorRef.current?.(error);
    } catch {
      // A consumer observer must not turn optional position persistence into
      // a fatal render path.
    }
  }, []);

  const resolveStorage = useCallback((): FloatingControlStorage | null => {
    if (storage === null) return null;
    if (storage !== undefined) return storage;
    try {
      return shellRef.current?.ownerDocument.defaultView?.localStorage ?? null;
    } catch (error) {
      reportStorageError(error);
      return null;
    }
  }, [reportStorageError, storage]);

  const setPosition = useCallback((next: Position) => {
    const current = positionRef.current;
    if (current?.left === next.left && current.top === next.top) return;
    positionRef.current = next;
    setPositionState(next);
    onPositionChangeRef.current?.(next);
  }, []);

  const persistPosition = useCallback(
    (next: Position, viewport: ViewportSize) => {
      const target = resolveStorage();
      if (!target || !storageKey) return;
      try {
        target.setItem(
          storageKey,
          JSON.stringify(toStoredPosition(next.left, next.top, viewport, positionVersion)),
        );
      } catch (error) {
        reportStorageError(error);
      }
    },
    [positionVersion, reportStorageError, resolveStorage, storageKey],
  );

  const readPosition = useCallback(() => {
    const target = resolveStorage();
    if (!target || !storageKey) return null;
    try {
      return parseStoredPosition(target.getItem(storageKey), positionVersion);
    } catch (error) {
      reportStorageError(error);
      return null;
    }
  }, [positionVersion, reportStorageError, resolveStorage, storageKey]);

  const clampPosition = useCallback(
    (next: Position, node: HTMLElement, size?: Size): Position => {
      return clampShellPosition(
        next,
        size ?? sizeFor(node, isCollapsed),
        viewportFor(node),
        isCollapsed,
        offsetX,
        offsetY,
      );
    },
    [isCollapsed, offsetX, offsetY],
  );

  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const viewport = viewportFor(node);
    const size = sizeFor(node, isCollapsed);

    const storageIdentity = `${storageKey}\u0000${positionVersion}`;
    if (
      !initializedRef.current ||
      storageIdentityRef.current !== storageIdentity ||
      storageSourceRef.current !== storage
    ) {
      const stored = readPosition();
      const initial = stored
        ? { left: stored.xRatio * viewport.width, top: stored.yRatio * viewport.height }
        : {
            left: side === 'left' ? offsetX : viewport.width - size.width - offsetX,
            top: vertical === 'top' ? offsetY : viewport.height - size.height - offsetY,
          };
      const next = clampShellPosition(initial, size, viewport, isCollapsed, offsetX, offsetY);
      initializedRef.current = true;
      storageIdentityRef.current = storageIdentity;
      storageSourceRef.current = storage;
      anchorRef.current = anchorFromPosition(next, size, viewport);
      setPosition(next);
      return;
    }

    const current = positionRef.current;
    if (!current) return;
    const anchor = anchorRef.current ?? anchorFromPosition(current, size, viewport);
    const next = clampShellPosition(
      positionFromAnchor(anchor, size, viewport),
      size,
      viewport,
      isCollapsed,
      offsetX,
      offsetY,
    );
    setPosition(next);
  }, [
    isCollapsed,
    offsetX,
    offsetY,
    positionVersion,
    readPosition,
    setPosition,
    side,
    storageKey,
    storage,
    vertical,
  ]);

  useEffect(() => {
    const node = shellRef.current;
    const view = node?.ownerDocument.defaultView;
    if (!node || !view) return;
    const keepReachable = () => {
      const viewport = viewportFor(node);
      const size = sizeFor(node, isCollapsed);
      const current = positionRef.current;
      if (!current) return;
      const anchor = anchorRef.current ?? anchorFromPosition(current, size, viewport);
      const next = clampShellPosition(
        positionFromAnchor(anchor, size, viewport),
        size,
        viewport,
        isCollapsed,
        offsetX,
        offsetY,
      );
      setPosition(next);
    };
    view.addEventListener('resize', keepReachable);
    const observer =
      typeof view.ResizeObserver === 'function' ? new view.ResizeObserver(keepReachable) : null;
    observer?.observe(node);
    return () => {
      view.removeEventListener('resize', keepReachable);
      observer?.disconnect();
    };
  }, [isCollapsed, offsetX, offsetY, setPosition]);

  const updateCollapsed = useCallback(
    (next: boolean) => {
      focusAfterToggleRef.current = next ? 'collapsed' : 'expanded';
      if (collapsed === undefined) setInternalCollapsed(next);
      onCollapsedChange?.(next);
    },
    [collapsed, onCollapsedChange],
  );

  useLayoutEffect(() => {
    const requested = focusAfterToggleRef.current;
    if (requested === 'collapsed' && isCollapsed) {
      focusAfterToggleRef.current = null;
      collapsedButtonRef.current?.focus();
    } else if (requested === 'expanded' && !isCollapsed) {
      focusAfterToggleRef.current = null;
      collapseButtonRef.current?.focus();
    }
  }, [isCollapsed]);

  useLayoutEffect(() => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    try {
      drag.captureTarget.releasePointerCapture?.(drag.pointerId);
    } catch {
      // The old conditional drag target may already have been detached.
    }
    setDragging(false);
    suppressCollapsedClickRef.current = false;
  }, [isCollapsed]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0 || event.isPrimary === false) return;
      if (dragRef.current) return;
      const node = shellRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      dragRef.current = {
        pointerId: event.pointerId,
        captureTarget: event.currentTarget,
        startX: event.clientX,
        startY: event.clientY,
        originLeft: positionRef.current?.left ?? rect.left,
        originTop: positionRef.current?.top ?? rect.top,
        size: sizeFor(node, isCollapsed),
        startedCollapsed: isCollapsed,
        moved: false,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [isCollapsed],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      const node = shellRef.current;
      if (!drag || !node || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (!drag.moved) {
        drag.moved = true;
        setDragging(true);
      }
      event.preventDefault();
      setPosition(
        clampPosition({ left: drag.originLeft + dx, top: drag.originTop + dy }, node, drag.size),
      );
    },
    [clampPosition, setPosition],
  );

  const finishPointer = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      const node = shellRef.current;
      if (!drag || !node || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      } catch {
        // Pointer capture may already be released after a browser cancellation.
      }
      setDragging(false);
      if (!drag.moved) return;
      suppressCollapsedClickRef.current = drag.startedCollapsed;
      const current = positionRef.current ?? { left: drag.originLeft, top: drag.originTop };
      // Content can grow while the pointer is down. Cache dimensions for
      // pointermove performance, then measure once at release so the final
      // persisted position is guaranteed reachable for the actual shell.
      const size = sizeFor(node, isCollapsed);
      const viewport = viewportFor(node);
      const snapped = snapToEdges(
        {
          left: current.left,
          top: current.top,
          right: current.left + size.width,
          bottom: current.top + size.height,
          width: size.width,
          height: size.height,
        },
        viewport,
        offsetX,
        offsetY,
        EDGE_SNAP_PX,
      );
      const finalPosition = clampShellPosition(
        snapped,
        size,
        viewport,
        isCollapsed,
        offsetX,
        offsetY,
      );
      anchorRef.current = anchorFromPosition(finalPosition, size, viewport);
      setPosition(finalPosition);
      persistPosition(finalPosition, viewport);
    },
    [isCollapsed, offsetX, offsetY, persistPosition, setPosition],
  );

  const cancelPointer = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      const node = shellRef.current;
      if (!drag || !node || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      } catch {
        // Browser cancellation can release capture before React dispatches.
      }
      setDragging(false);
      suppressCollapsedClickRef.current = false;
      if (!drag.moved) return;
      const viewport = viewportFor(node);
      const size = sizeFor(node, isCollapsed);
      const origin = clampShellPosition(
        { left: drag.originLeft, top: drag.originTop },
        size,
        viewport,
        isCollapsed,
        offsetX,
        offsetY,
      );
      anchorRef.current = anchorFromPosition(origin, size, viewport);
      setPosition(origin);
    },
    [isCollapsed, offsetX, offsetY, setPosition],
  );

  const onMoveKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const node = shellRef.current;
      if (!node || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      const step = event.shiftKey ? KEYBOARD_FINE_STEP_PX : KEYBOARD_STEP_PX;
      const current = positionRef.current ?? { left: 0, top: 0 };
      const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
      const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
      const next = clampPosition({ left: current.left + dx, top: current.top + dy }, node);
      const viewport = viewportFor(node);
      anchorRef.current = anchorFromPosition(next, sizeFor(node, isCollapsed), viewport);
      setPosition(next);
      persistPosition(next, viewport);
    },
    [clampPosition, isCollapsed, persistPosition, setPosition],
  );

  const dragHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: finishPointer,
    onPointerCancel: cancelPointer,
    onLostPointerCapture: cancelPointer,
    onKeyDown: onMoveKeyDown,
  };

  const shellClassName = ['vd-floating-control', className].filter(Boolean).join(' ');
  const positionedStyle: CSSProperties = position
    ? { ...style, left: position.left, top: position.top }
    : (style ?? {});
  const normalizedBadge =
    typeof badgeCount === 'number' && Number.isFinite(badgeCount) ? Math.floor(badgeCount) : 0;
  const visibleBadge =
    normalizedBadge > 0
      ? normalizedBadge > 99
        ? '99+'
        : String(normalizedBadge)
      : null;

  return (
    <div
      ref={shellRef}
      className={shellClassName}
      data-collapsed={isCollapsed ? 'true' : 'false'}
      data-dragging={dragging ? 'true' : 'false'}
      style={positionedStyle}
      role={isCollapsed ? undefined : 'region'}
      aria-label={isCollapsed ? undefined : label}
    >
      {isCollapsed ? (
        <button
          ref={collapsedButtonRef}
          type="button"
          className="vd-floating-control__collapsed"
          aria-label={`Open ${label}`}
          aria-expanded="false"
          onClick={(event) => {
            if (suppressCollapsedClickRef.current) {
              suppressCollapsedClickRef.current = false;
              event.preventDefault();
              return;
            }
            updateCollapsed(false);
          }}
          {...dragHandlers}
        >
          <span className="vd-floating-control__icon" aria-hidden="true">
            {icon}
          </span>
          {visibleBadge ? <span className="vd-floating-control__badge">{visibleBadge}</span> : null}
        </button>
      ) : (
        <>
          <div className="vd-floating-control__header">
            <button
              type="button"
              className="vd-floating-control__drag-handle"
              aria-label={`Move ${label}. Use arrow keys to reposition.`}
              title="Drag to move"
              {...dragHandlers}
            >
              <span aria-hidden="true">⠿</span>
            </button>
            <span className="vd-floating-control__icon" aria-hidden="true">
              {icon}
            </span>
            <strong className="vd-floating-control__title">{label}</strong>
            {visibleBadge ? (
              <span className="vd-floating-control__count">{visibleBadge}</span>
            ) : null}
            <div className="vd-floating-control__header-actions">{headerActions}</div>
            <button
              ref={collapseButtonRef}
              type="button"
              className="vd-floating-control__collapse"
              aria-label={`Collapse ${label}`}
              aria-expanded="true"
              onClick={() => updateCollapsed(true)}
            >
              <span aria-hidden="true">⌄</span>
            </button>
          </div>
          <div className="vd-floating-control__content">{children}</div>
        </>
      )}
    </div>
  );
}
